// Manager Approval Request Card Component (VAC-8)
// Displays individual vacation request in approval queue

import React from 'react'
import { CheckCircle, XCircle, Eye, Clock, Calendar } from 'lucide-react'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { ApprovalQueueItem } from '@/types/vacation'

interface ApprovalRequestCardProps {
  request: ApprovalQueueItem
  onApprove: (requestId: string) => void
  onReject: (requestId: string) => void
  onViewDetails: (requestId: string) => void
  className?: string
}

export const ApprovalRequestCard: React.FC<ApprovalRequestCardProps> = ({
  request,
  onApprove,
  onReject,
  onViewDetails,
  className = ''
}) => {
  const vacationTypeLabel = {
    FULL: 'Férias Completas',
    SPLIT: 'Férias Fracionadas',
    SELL_THIRD: 'Venda de 1/3'
  }[request.type]

  const vacationTypeBadgeColor = {
    FULL: 'bg-info/10 border border-info/30 text-info',
    SPLIT: 'bg-brand-primary/10 border border-brand-primary/30 text-brand-primary',
    SELL_THIRD: 'bg-warning/10 border border-warning/30 text-warning'
  }[request.type]

  const formatDate = (isoDate: string): string => {
    return format(parseISO(isoDate), "dd 'de' MMM", { locale: ptBR })
  }

  // Get initials for avatar fallback
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  return (
    <div
      className={`card-hover p-6 ${className}`}
    >
      {/* Employee Header */}
      <div className="flex items-center gap-3 mb-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {request.employeeAvatar ? (
            <img
              src={request.employeeAvatar}
              alt={request.employeeName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center text-brand-dark font-semibold">
              {getInitials(request.employeeName)}
            </div>
          )}
        </div>

        {/* Employee Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-display font-semibold text-white truncate">
            {request.employeeName}
          </h3>
          <p className="text-sm text-brand-gray truncate">
            {request.employeeDepartment && request.employeeRole
              ? `${request.employeeDepartment} • ${request.employeeRole}`
              : request.employeeDepartment || request.employeeRole || 'Colaborador'}
          </p>
        </div>

        {/* Expiring Badge */}
        {request.balanceExpiringSoon && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 border border-warning/30 text-warning text-xs font-medium rounded-full">
              <Clock className="w-3 h-3" />
              Saldo Expirando
            </span>
          </div>
        )}
      </div>

      {/* Request Details */}
      <div className="mb-4 space-y-2">
        {/* Date Range */}
        <div className="flex items-center gap-2 text-white">
          <Calendar className="w-4 h-4 text-brand-gray" />
          <span className="font-medium">
            {formatDate(request.startDate)} → {formatDate(request.endDate)}
          </span>
          <span className="text-brand-gray">
            ({request.totalDays} {request.totalDays === 1 ? 'dia' : 'dias'})
          </span>
        </div>

        {/* Vacation Type Badge */}
        <div className="flex items-center gap-2">
          <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${vacationTypeBadgeColor}`}>
            {vacationTypeLabel}
          </span>
        </div>

        {/* Submission Time */}
        <p className="text-xs text-brand-gray flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Enviado {formatDistanceToNow(parseISO(request.submittedAt), { locale: ptBR, addSuffix: true })}
        </p>
      </div>

      {/* Employee Notes (if provided) */}
      {request.employeeNotes && (
        <div className="mb-4 p-3 bg-brand-dark-lighter rounded-monumenta">
          <p className="text-sm text-white italic line-clamp-2">
            "{request.employeeNotes}"
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => onApprove(request.requestId)}
          className="btn-primary flex-1 min-w-[100px] inline-flex items-center justify-center gap-2"
          aria-label={`Aprovar férias de ${request.employeeName}`}
        >
          <CheckCircle className="w-4 h-4" />
          <span>Aprovar</span>
        </button>

        <button
          onClick={() => onReject(request.requestId)}
          className="btn-danger flex-1 min-w-[100px] inline-flex items-center justify-center gap-2"
          aria-label={`Rejeitar férias de ${request.employeeName}`}
        >
          <XCircle className="w-4 h-4" />
          <span>Rejeitar</span>
        </button>

        <button
          onClick={() => onViewDetails(request.requestId)}
          className="btn-secondary flex-1 min-w-[100px] inline-flex items-center justify-center gap-2"
          aria-label={`Ver detalhes da solicitação de ${request.employeeName}`}
        >
          <Eye className="w-4 h-4" />
          <span>Ver Detalhes</span>
        </button>
      </div>
    </div>
  )
}

/**
 * Skeleton loader for ApprovalRequestCard
 */
export const ApprovalRequestCardSkeleton: React.FC = () => {
  return (
    <div className="card animate-pulse p-6">
      {/* Header Skeleton */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-brand-dark-lighter" />
        <div className="flex-1">
          <div className="h-5 bg-brand-dark-lighter rounded w-3/4 mb-2" />
          <div className="h-4 bg-brand-dark-lighter rounded w-1/2" />
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="space-y-3 mb-4">
        <div className="h-4 bg-brand-dark-lighter rounded w-full" />
        <div className="h-4 bg-brand-dark-lighter rounded w-2/3" />
        <div className="h-4 bg-brand-dark-lighter rounded w-1/2" />
      </div>

      {/* Buttons Skeleton */}
      <div className="flex gap-2">
        <div className="flex-1 h-10 bg-brand-dark-lighter rounded-monumenta" />
        <div className="flex-1 h-10 bg-brand-dark-lighter rounded-monumenta" />
        <div className="flex-1 h-10 bg-brand-dark-lighter rounded-monumenta" />
      </div>
    </div>
  )
}
